/**
 * Core Error Capture
 *
 * Main error capture and reporting module. Captures errors with full context including
 * stack traces, breadcrumbs, user info, and environment data, then sends to Firebase
 * Cloud Functions for processing and storage.
 *
 * Features:
 * - Automatic severity and category inference
 * - Sampling for warnings and info messages
 * - Stack trace parsing for file/line/column
 * - Session tracking
 * - User context management
 * - Configurable behavior
 *
 * @module lib/datahub/errorLogging/captureError
 *
 * @example
 * ```typescript
 * import { captureError, logError, configureErrorLogging, setUserContext } from './captureError';
 *
 * // Configure on app startup
 * configureErrorLogging({
 *   enabled: true,
 *   logToConsole: process.env.NODE_ENV !== 'production'
 * });
 *
 * // Set user context after auth
 * setUserContext('user-123', 'user@example.com');
 *
 * // Capture errors in try-catch
 * try {
 *   riskyOperation();
 * } catch (error) {
 *   await logError(error, { severity: 'error', componentName: 'CheckoutForm' });
 * }
 * ```
 */

import { getFunctions } from 'firebase/functions';
import { httpsCallable } from '../../firebase/authReadyCallable';
import * as Sentry from '@sentry/react';
import type {
  ErrorSeverity,
  ErrorCategory,
  CapturePoint,
  ErrorPayload,
  CaptureErrorInput,
  ErrorLoggingConfig,
} from './types';
import { DEFAULT_CONFIG } from './types';
import { getBreadcrumbs } from './breadcrumbs';
import { deviceReportsOffline, matchDeviceOfflinePattern } from './deviceOfflineNoise';
import { APP_VERSION } from '../../../config/appVersion';
import { tabVisibility } from './tabVisibility';

// Session ID - unique per browser session
const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

// Circuit breaker: prevent recursive captureError calls
// When captureError's own network call fails, the console.error in the catch
// can trigger global error handlers → captureError again → infinite loop
let isSendingError = false;

/**
 * Errors that have ALREADY been handed to Sentry by this module.
 *
 * Why this exists: an error boundary reports the crash here (real error, real
 * stack, real componentStack) and THEN `console.error`s it for the dev console.
 * That second call goes through the contextCapture interceptor, which reports it
 * AGAIN — as a fresh `Error` built inside the interceptor, so the duplicate
 * carries the INTERCEPTOR's stack. One crash, two Sentry events, and the louder
 * of the two is the one that names our logging code as the culprit.
 *
 * A `WeakSet` keyed on the error OBJECT is the right identity here: it is exactly
 * "this same throw, seen twice", it needs no message parsing, and it holds no
 * reference that could keep a crashed component's closure alive.
 *
 * 🔴 MEMBERSHIP IS ADDED ONLY AFTER `Sentry.captureException` HAS RETURNED
 * WITHOUT THROWING, never before. Marking first would create the inverse defect
 * — an error recorded as "already reported" that never actually reached Sentry —
 * which is SILENT LOSS, strictly worse than the duplicate this guard removes.
 * The ordering is the whole safety argument, so it is asserted by a test that
 * forces `captureException` to throw and requires the console path to still
 * report (see __tests__/errorIdentity.test.ts, "guard does not latch on a failed
 * send").
 *
 * ⚠️ THE ASSUMPTION THIS RESTS ON: **one Error object == one occurrence.** There
 * is no expiry — membership lasts as long as the object does. Every boundary and
 * every throw site in the app today constructs or catches a fresh Error per
 * incident, so that holds. It would STOP holding if a caller reused one Error
 * instance across genuinely separate incidents — a module-level sentinel Error,
 * a memoised rejection, or a retry loop that re-logs the single error object it
 * caught on attempt one. In that shape the second, real failure is silently
 * dropped. If you are about to reuse an Error instance, construct a new one
 * instead, or this guard will eat the report.
 */
const reportedToSentry = new WeakSet<object>();

/**
 * True when this exact Error object has already been delivered to Sentry by
 * `captureError`. Consumed by the console interceptor so a boundary-caught crash
 * is reported exactly once.
 *
 * Fails OPEN: anything that is not an object (a thrown string, `undefined`) is
 * never a member, so it reports. Duplicate-but-visible beats deduped-but-lost.
 */
export function wasReportedToSentry(error: unknown): boolean {
  return typeof error === 'object' && error !== null && reportedToSentry.has(error);
}

// Track current user info
let currentUserId: string | undefined;
let currentUserEmail: string | undefined;
let currentRoute: string = window.location.pathname;

// Configuration
let config: ErrorLoggingConfig = { ...DEFAULT_CONFIG };

/**
 * Configure error logging
 *
 * Updates the error logging configuration. Call this at app initialization
 * to customize behavior.
 *
 * @param newConfig - Partial config object with fields to override
 *
 * @example
 * ```typescript
 * configureErrorLogging({
 *   enabled: process.env.NODE_ENV === 'production',
 *   logToConsole: true,
 *   warningSampleRate: 0.1, // 10% of warnings
 *   infoSampleRate: 0.01    // 1% of info messages
 * });
 * ```
 */
export function configureErrorLogging(newConfig: Partial<ErrorLoggingConfig>): void {
  config = { ...config, ...newConfig };
}

/**
 * Set the current user context
 */
export function setUserContext(userId?: string, email?: string): void {
  currentUserId = userId;
  currentUserEmail = email;
  if (userId) {
    Sentry.setUser({ id: userId, email: email || undefined });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Update the current route
 */
export function setCurrentRoute(route: string): void {
  currentRoute = route;
}

/**
 * Get the current session ID
 */
export function getSessionId(): string {
  return sessionId;
}

/**
 * Determine severity based on error type if not provided
 */
function inferSeverity(_error: Error | unknown, capturePoint: CapturePoint): ErrorSeverity {
  // React render errors are typically critical
  if (capturePoint === 'error_boundary') {
    return 'critical';
  }

  // Unhandled errors are errors
  if (capturePoint === 'window_onerror' || capturePoint === 'unhandled_rejection') {
    return 'error';
  }

  // Network errors depend on status
  if (capturePoint === 'network_interceptor') {
    return 'error';
  }

  return 'error';
}

/**
 * Determine category based on error type if not provided
 */
function inferCategory(error: Error | unknown, capturePoint: CapturePoint): ErrorCategory {
  const errorObj = error instanceof Error ? error : new Error(String(error));

  // Check capture point first
  if (capturePoint === 'error_boundary') return 'react_render';
  if (capturePoint === 'unhandled_rejection') return 'unhandled_promise';
  if (capturePoint === 'window_onerror') return 'uncaught_exception';
  if (capturePoint === 'network_interceptor') return 'network_request';

  // Check error message/name for clues
  const message = errorObj.message.toLowerCase();
  const name = errorObj.name.toLowerCase();

  if (message.includes('firebase') || name.includes('firebase')) {
    if (message.includes('auth')) return 'firebase_auth';
    if (message.includes('firestore')) return 'firebase_firestore';
    if (message.includes('function')) return 'firebase_functions';
  }

  if (message.includes('sync')) return 'sync_error';
  if (message.includes('validation') || message.includes('invalid')) return 'validation_error';
  if (message.includes('network') || message.includes('fetch')) return 'network_request';

  return 'unknown';
}

/**
 * Parse stack trace to extract file, line, column
 */
function parseStackTrace(stack?: string): {
  file?: string;
  line?: number;
  column?: number;
  functionName?: string;
} {
  if (!stack) return {};

  // Match patterns like:
  // at functionName (file.js:123:45)
  // at file.js:123:45
  const stackLines = stack.split('\n');

  for (const line of stackLines) {
    // Skip the error message line
    if (!line.trim().startsWith('at')) continue;

    // Try to extract location
    const match = line.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?/);
    if (match) {
      return {
        functionName: match[1] || undefined,
        file: match[2],
        line: parseInt(match[3], 10),
        column: parseInt(match[4], 10),
      };
    }
  }

  return {};
}

/**
 * Get browser info
 */
function getBrowserInfo(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
}

/**
 * Get device type
 */
function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent;
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  if (/mobile|iphone|android/i.test(ua)) return 'mobile';
  return 'desktop';
}

/**
 * Determine if error should be sampled
 */
function shouldSample(severity: ErrorSeverity): boolean {
  if (severity === 'critical' || severity === 'error') {
    return true; // Always capture critical and error
  }

  if (severity === 'warning') {
    return Math.random() < config.warningSampleRate;
  }

  if (severity === 'info') {
    return Math.random() < config.infoSampleRate;
  }

  return true;
}

/**
 * Main error capture function
 *
 * Captures an error with full context and sends it to the backend.
 * Automatically infers severity and category if not provided.
 * Applies sampling based on severity. Parses stack traces and enriches
 * with user context, breadcrumbs, and environment data.
 *
 * @param input - Error capture input with error object and metadata
 *
 * @example
 * ```typescript
 * await captureError({
 *   error: new Error('Failed to load data'),
 *   severity: 'error',
 *   category: 'network_request',
 *   capturePoint: 'try_catch',
 *   componentName: 'DataTable',
 *   additionalContext: { connectionId: 'conn-123' }
 * });
 * ```
 */
export async function captureError(input: CaptureErrorInput): Promise<void> {
  if (!config.enabled) return;

  // Circuit breaker: if we're already sending an error, don't recurse
  if (isSendingError) return;

  const errorObj = input.error instanceof Error ? input.error : new Error(String(input.error));
  // The device had no connection: not our fault, the SDK retries by itself (deviceOfflineNoise.ts). Only while the device reports offline, and the drop leaves a breadcrumb.
  const offlinePattern = matchDeviceOfflinePattern(errorObj.message);
  if (offlinePattern !== null && deviceReportsOffline()) {
    Sentry.addBreadcrumb({ category: 'offline-noise', level: 'info', message: offlinePattern });
    return;
  }
  const severity = input.severity || inferSeverity(input.error, input.capturePoint);
  const category = input.category || inferCategory(input.error, input.capturePoint);

  // Apply sampling
  if (!shouldSample(severity)) {
    if (config.logToConsole) {
      console.debug('[ErrorLogging] Sampled out:', errorObj.message);
    }
    return;
  }

  const stackInfo = parseStackTrace(errorObj.stack);

  const payload: ErrorPayload = {
    message: errorObj.message,
    name: errorObj.name,
    stack: errorObj.stack,
    code: (input.error as { code?: string }).code,
    severity,
    category,
    source: 'client',
    capturePoint: input.capturePoint,
    file: stackInfo.file,
    line: stackInfo.line,
    column: stackInfo.column,
    functionName: stackInfo.functionName,
    componentName: input.componentName,
    userId: currentUserId,
    userEmail: currentUserEmail,
    sessionId,
    currentRoute,
    breadcrumbs: getBreadcrumbs(),
    environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
    browser: getBrowserInfo(),
    deviceType: getDeviceType(),
    appVersion: APP_VERSION || '0.0.0',
    userAgent: navigator.userAgent,
    // `online` is recorded on every report so the Dev Issues alert can say whether the DEVICE had a
    // network when this failed (navigator.onLine is coarse but is the signal the browser gives).
    // A caller-supplied `online` wins, so a test or a deliberate override is never clobbered.
    additionalContext: { online: typeof navigator !== 'undefined' ? navigator.onLine : undefined, visibility: tabVisibility(), ...input.additionalContext },
  };

  // Log to console in development
  if (config.logToConsole) {
    console.group(`[ErrorLogging] ${severity.toUpperCase()}: ${errorObj.name}`);
    // Use console.warn (not .error) to avoid triggering the
    // contextCapture → handleConsoleError → captureError re-entrancy loop.
    console.warn('Captured error:', errorObj);
    console.log('Payload:', payload);
    console.groupEnd();
  }

  // A React component stack belongs in Sentry's `react` CONTEXT, not buried in
  // `extra`. Sentry renders that context as a component tree next to the stack
  // trace; the same string in `extra` is an unformatted blob nobody expands.
  // The boundaries already pass it — this is only about where it lands.
  const componentStack =
    typeof input.additionalContext?.componentStack === 'string'
      ? input.additionalContext.componentStack
      : undefined;

  // Send to Sentry (independent of Firestore, has its own circuit breaker)
  try {
    Sentry.captureException(errorObj, {
      tags: {
        // Caller tags FIRST so the three below always win. These three are what
        // the triage UI and every saved Sentry search key on; a caller that
        // could overwrite `category` would silently move its events out of
        // every filter that looks for them.
        ...input.tags,
        category,
        capturePoint: input.capturePoint,
        source: 'client',
      },
      // An explicit fingerprint is set ONLY by callers that know frame-based
      // grouping is wrong for their event — today, the network interceptor,
      // whose Error is synthesised at a single site so every endpoint in the app
      // shares one stack and therefore one Sentry issue. Everything else keeps
      // Sentry's default grouping, which is correct once the stack is real.
      ...(input.fingerprint ? { fingerprint: [...input.fingerprint] } : {}),
      ...(componentStack ? { contexts: { react: { componentStack } } } : {}),
      extra: {
        route: currentRoute,
        componentName: input.componentName,
        ...input.additionalContext,
      },
    });

    // Mark ONLY here — after a delivery that did not throw. See reportedToSentry.
    if (typeof input.error === 'object' && input.error !== null) {
      reportedToSentry.add(input.error);
    }
    if (errorObj !== input.error) {
      // A non-Error input (a thrown string) was wrapped above. Mark the wrapper
      // too, so a caller holding it cannot re-report the same delivery.
      reportedToSentry.add(errorObj);
    }
  } catch {
    // Never let Sentry break error logging — and never mark on this path.
  }

  // Send to backend — unless this caller has said the class is already
  // aggregated in Sentry and must not also mint an `error_groups` triage
  // document. See `skipErrorGroups` in types.ts for the (narrow) contract.
  if (input.skipErrorGroups) return;

  isSendingError = true;
  try {
    const functions = getFunctions();
    const logErrorFn = httpsCallable(functions, 'logError');
    await logErrorFn(payload);

    if (config.logToConsole) {
      console.debug('[ErrorLogging] Error sent to backend');
    }
  } catch (sendError) {
    // Use console.warn (not .error) to avoid triggering window.onerror → captureError loop.
    // Always log failures — even in production — so we know error tracking is broken.
    console.warn('[ErrorLogging] Failed to send error to backend:', (sendError as Error)?.message || sendError);
  } finally {
    isSendingError = false;
  }
}

/**
 * Convenience function for capturing errors with less boilerplate
 *
 * Simplified error logging function for try-catch blocks. Automatically
 * sets capturePoint to 'try_catch' and accepts optional metadata.
 *
 * @param error - The error object to capture
 * @param options - Optional severity, category, componentName, and context
 * @returns Promise that resolves when error is sent
 *
 * @example
 * ```typescript
 * try {
 *   await syncConnection(id);
 * } catch (error) {
 *   await logError(error, {
 *     severity: 'error',
 *     componentName: 'ConnectionSync',
 *     additionalContext: { connectionId: id }
 *   });
 *   throw error; // Re-throw if needed
 * }
 * ```
 */
export function logError(
  error: Error | unknown,
  options?: {
    severity?: ErrorSeverity;
    category?: ErrorCategory;
    componentName?: string;
    additionalContext?: Record<string, unknown>;
  }
): Promise<void> {
  return captureError({
    error,
    capturePoint: 'try_catch',
    ...options,
  });
}

